import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const INK = '#14261F';
const PRIMARY = '#1E5C46';
const PRIMARY_STRONG = '#174A38';
const PRIMARY_SOFT = '#E7F0EC';
const ACCENT = '#D9A441';
const MUTED = '#66736C';
const BORDER = '#E3E8E3';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message || 'Login failed');
    }

    setLoading(false);
  };

  const fieldSx = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      '& fieldset': { borderColor: BORDER },
      '&:hover fieldset': { borderColor: PRIMARY },
      '&.Mui-focused fieldset': { borderColor: PRIMARY },
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${PRIMARY_SOFT}`,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      }}
    >
      <Box
        sx={{
          bgcolor: INK,
          color: '#F3F6F4',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { xs: 3.5, md: 6 },
          minHeight: { xs: 180, md: '100vh' },
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Archivo", sans-serif',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: '0.14em',
          }}
        >
          ATU
          <Box component="span" sx={{ color: ACCENT }}>
            ·
          </Box>
          TEIN
        </Typography>

        <Box>
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Archivo", sans-serif',
              fontWeight: 600,
              fontSize: { xs: 24, md: 34 },
              lineHeight: 1.2,
              maxWidth: 420,
              letterSpacing: '-0.01em',
            }}
          >
            One registry. Every member, record and election.
          </Typography>
          <Typography
            sx={{
              color: '#A8B5AE',
              mt: 1.75,
              maxWidth: 400,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            The ATUTEIN Data Management System keeps membership, HR and election
            records accurate, auditable and in one place.
          </Typography>
        </Box>

        <Typography sx={{ fontSize: 12, color: '#6F7E76' }}>
          © 2026 ATUTEIN · Accra, Ghana
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 4 },
          bgcolor: '#FFFFFF',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Archivo", sans-serif',
              fontWeight: 600,
              fontSize: 24,
              mb: 0.75,
              letterSpacing: '-0.01em',
              color: INK,
            }}
          >
            Sign in
          </Typography>
          <Typography sx={{ color: MUTED, mb: 3.5, fontSize: 14 }}>
            Use your staff account to continue.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: '9px',
                bgcolor: '#FBEDE9',
                color: '#7C2E1D',
                border: '1px solid #EFCFC5',
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="name@atutein.org"
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={fieldSx}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 1,
                mb: 2,
                py: 1.25,
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: 14,
                bgcolor: PRIMARY,
                boxShadow: 'none',
                textTransform: 'none',
                '&:hover': { bgcolor: PRIMARY_STRONG, boxShadow: 'none' },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${ACCENT}`,
                  outlineOffset: 2,
                },
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography sx={{ color: MUTED, mb: 1.25, fontSize: 13 }}>
              Are you a member?
            </Typography>
            <Button
              component={Link}
              to="/member-portal/login"
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                borderRadius: '8px',
                borderColor: BORDER,
                color: INK,
                textTransform: 'none',
                fontWeight: 600,
                py: 1,
                '&:hover': { bgcolor: '#F6F8F6', borderColor: BORDER },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${ACCENT}`,
                  outlineOffset: 2,
                },
              }}
            >
              Go to Member Portal
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
